<template>
    <div class="supplier-verification">
        <b-alert
            v-if="error"
            variant="danger"
            show
        >
            <div class="alert-body">
                {{ error }}
            </div>
        </b-alert>

        <UnclassifiedVerification
            :id="id"
            :product="product"
            document-space="supplier"
            :enable-polling="true"
            @refreshDocuments="refreshSupplierDocuments"
        />

        <SupplierVerificationSection
            :title="$t('Component(Verification):compliant-statement-label')"
            :empty="isEmpty('compliantStatement')"
            :empty-text="$t('Component(Text):there-are-no-documents')"
            theme="supplier"
            addable
            @add="addCompliantStatement"
        >
            <div
                v-for="document in supplierDocuments.compliantStatement"
                :key="document.id"
                class="row mb-1 document-row"
            >
                <div class="col-lg-11">
                    <CompliantStatement
                        :declaration="document"
                        :legislations="legislations"
                        :compact-view="compactView"
                        @change="markSupplierVerificationDocumentsDirty"
                        @download="downloadSupplierDocument"
                        @preview="previewSupplierDocument"
                    />
                </div>
                <div
                    class="col-lg-1"
                    :class="compactView ? 'my-auto' : 'mt-auto'"
                >
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeSupplierDocument('compliantStatement', document)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
            <SupplierVerificationReplyRow
                v-for="document in replyDocuments.compliantStatement"
                :key="`reply-${document.id}`"
                :document="document"
                @download="downloadReplyDocument"
                @preview="previewReplyDocument"
            />
        </SupplierVerificationSection>

        <SupplierVerificationSection
            :title="$t('Component(Verification):doc-label')"
            :empty="isEmpty('declarationsOfConformity')"
            :empty-text="$t('Component(Text):there-are-no-documents')"
            theme="supplier"
            addable
            @add="addDeclarationOfConformity"
        >
            <div
                v-for="document in supplierDocuments.declarationsOfConformity"
                :key="document.id"
                class="row mb-1 document-row"
            >
                <div class="col-lg-11">
                    <DeclarationOfConformity
                        :declaration="document"
                        :legislations="legislations"
                        :compact-view="compactView"
                        @change="markSupplierVerificationDocumentsDirty"
                        @download="downloadSupplierDocument"
                        @preview="previewSupplierDocument"
                    />
                </div>
                <div
                    class="col-lg-1"
                    :class="compactView ? 'my-auto' : 'mt-auto'"
                >
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeSupplierDocument('declarationsOfConformity', document)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
            <SupplierVerificationReplyRow
                v-for="document in replyDocuments.declarationsOfConformity"
                :key="`reply-${document.id}`"
                :document="document"
                @download="downloadReplyDocument"
                @preview="previewReplyDocument"
            />
        </SupplierVerificationSection>

        <SupplierVerificationSection
            :title="$t('Component(Verification):evidence-report-label')"
            :empty="isEmpty('evidenceReports')"
            :empty-text="$t('Component(Text):there-are-no-documents')"
            theme="supplier"
            addable
            @add="addEvidenceReport"
        >
            <div
                v-for="document in supplierDocuments.evidenceReports"
                :key="document.id"
                class="row mb-1 document-row"
            >
                <div class="col-lg-11">
                    <EvidenceReport
                        :report="document"
                        :legislations="legislations"
                        :types="evidenceReportTypes"
                        :compact-view="compactView"
                        @change="markSupplierVerificationDocumentsDirty"
                        @download="downloadSupplierDocument"
                        @preview="previewSupplierDocument"
                    />
                </div>
                <div
                    class="col-lg-1"
                    :class="compactView ? 'my-auto' : 'mt-auto'"
                >
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeSupplierDocument('evidenceReports', document)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
            <SupplierVerificationReplyRow
                v-for="document in replyDocuments.evidenceReports"
                :key="`reply-${document.id}`"
                :document="document"
                @download="downloadReplyDocument"
                @preview="previewReplyDocument"
            />
        </SupplierVerificationSection>

        <b-row>
            <b-col>
                <b-card-group deck>
                    <SupplierVerificationInstruction
                        :title="$t('Component(Verification):safe-use-label')"
                        :supplier-instruction="supplierDocuments.safeUseInstructions"
                        :reply-instruction="replyDocuments.safeUseInstruction"
                        :allow-file="false"
                        theme="supplier"
                        @change="markSupplierVerificationDocumentsDirty"
                        @download-supplier="downloadSupplierDocument"
                        @preview-supplier="previewSupplierDocument"
                        @download-reply="downloadReplyDocument"
                        @preview-reply="previewReplyDocument"
                    />
                    <SupplierVerificationInstruction
                        :title="$t('Component(Verification):dissembling-label')"
                        :supplier-instruction="supplierDocuments.dissemblingInstructions"
                        :reply-instruction="replyDocuments.dissemblingInstruction"
                        theme="supplier"
                        removable
                        @change="markSupplierVerificationDocumentsDirty"
                        @download-supplier="downloadSupplierDocument"
                        @preview-supplier="previewSupplierDocument"
                        @download-reply="downloadReplyDocument"
                        @preview-reply="previewReplyDocument"
                        @remove-supplier="removeSupplierInstruction('dissemblingInstructions')"
                    />
                </b-card-group>
            </b-col>
        </b-row>

        <SupplierVerificationSection
            v-if="supplierDocuments.other.length"
            :title="$t('Component(VerificationDocumentTypeEnum):other')"
            theme="supplier"
        >
            <OtherVerificationDocumentRow
                v-for="document in supplierDocuments.other"
                :key="document.id"
                :document="document"
                @download="downloadSupplierDocument"
                @preview="previewSupplierDocument"
            />
        </SupplierVerificationSection>

        <DocumentPreview
            ref="supplierPreview"
            :product="product"
            :file="supplierPreviewFile"
        />
        <ReplyDocumentPreview
            ref="replyPreview"
            :file="replyPreviewFile"
            :request="replyRequest"
        />
    </div>
</template>

<script>
import {
    BAlert, BButton, BCardGroup, BCol, BRow,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { LegislationApi, ProductApi } from '@core/api'
import { ProductProvider } from '@core/domain/product'
import DownloadProductDocument from '@core/mixins/download-product-document'
import { DocumentPreview } from '@core/components/product'
import ReplyDocumentPreview from '@/views/request/sent/components/ReplyDocumentPreview.vue'
import DeclarationOfConformity from './verification/DeclarationOfConformity.vue'
import CompliantStatement from './verification/CompliantStatement.vue'
import EvidenceReport from './EvidenceReport.vue'
import UnclassifiedVerification from './verification/UnclassifiedVerification.vue'
import OtherVerificationDocumentRow from './verification/OtherVerificationDocumentRow.vue'
import SupplierVerificationInstruction from './supplier-verification/SupplierVerificationInstruction.vue'
import SupplierVerificationReplyRow from './supplier-verification/SupplierVerificationReplyRow.vue'
import SupplierVerificationSection from './supplier-verification/SupplierVerificationSection.vue'

export default {
    components: {
        BAlert,
        BButton,
        BCardGroup,
        BCol,
        BRow,
        CompliantStatement,
        DeclarationOfConformity,
        DocumentPreview,
        EvidenceReport,
        OtherVerificationDocumentRow,
        ReplyDocumentPreview,
        SupplierVerificationInstruction,
        SupplierVerificationReplyRow,
        SupplierVerificationSection,
        UnclassifiedVerification,
    },
    directives: { Ripple },
    mixins: [DownloadProductDocument],
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
        product: {
            type: Object,
            required: true,
        },
        compactView: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            legislations: [],
            evidenceReportTypes: [],
            supplierDocuments: ProductProvider(this.id).supplierVerificationDocuments,
            replyDocuments: ProductProvider(this.id).supplierVerificationReplyDocuments,
            supplierPreviewFile: null,
            replyPreviewFile: null,
            error: null,
        }
    },
    computed: {
        replyRequest() {
            return { id: this.replyDocuments.requestId }
        },
    },
    watch: {
        id() {
            this.supplierDocuments = ProductProvider(this.id).supplierVerificationDocuments
            this.replyDocuments = ProductProvider(this.id).supplierVerificationReplyDocuments
            this.fetch()
        },
        'supplierDocuments.declarationsOfConformity': function declarationsOfConformityChanged() {
            this.hydrateSupplierDocuments()
        },
        'supplierDocuments.compliantStatement': function compliantStatementChanged() {
            this.hydrateSupplierDocuments()
        },
        'supplierDocuments.evidenceReports': function evidenceReportsChanged() {
            this.hydrateSupplierDocuments()
        },
    },
    created() {
        this.fetch()
    },
    methods: {
        fetch() {
            this.error = null
            const productId = this.id
            const product = ProductProvider(productId)
            const supplierDocumentsPromise = product
                .getSupplierVerificationDocuments()
                .then(supplierDocuments => {
                    if (String(this.id) === String(productId)) {
                        this.supplierDocuments = supplierDocuments
                        this.$emit('ready', productId)
                    }

                    return supplierDocuments
                })

            return Promise.all([
                supplierDocumentsPromise,
                product.getSupplierVerificationReplyDocuments(),
                LegislationApi.all().then(r => r.data),
                LegislationApi.getEvidenceReportTypes().then(r => r.data),
            ])
                .then(([supplierDocuments, replyDocuments, legislations, evidenceReportTypes]) => {
                    if (String(this.id) !== String(productId)) {
                        return
                    }

                    this.supplierDocuments = supplierDocuments
                    this.replyDocuments = replyDocuments
                    this.legislations = legislations
                    this.evidenceReportTypes = evidenceReportTypes
                    this.hydrateSupplierDocuments()
                })
                .catch(error => {
                    if (String(this.id) === String(productId)) {
                        this.error = this.getErrorMessage(error)
                    }
                })
        },
        refreshSupplierDocuments() {
            const productId = this.id
            const product = ProductProvider(productId)

            if (product.supplierVerificationDocumentsDirty) {
                return
            }

            return product
                .getSupplierVerificationDocuments(true)
                .then(documents => {
                    if (String(this.id) !== String(productId)) {
                        return
                    }

                    this.supplierDocuments = documents
                    this.hydrateSupplierDocuments()
                    this.$emit('ready', productId)
                })
        },
        hydrateSupplierDocuments() {
            if (this.legislations.length) {
                this.supplierDocuments.declarationsOfConformity.forEach(document => {
                    this.$set(document, 'legislations', this.hydrateReferenceValues(document.legislations, this.legislations))
                })
                this.supplierDocuments.compliantStatement.forEach(document => {
                    this.$set(document, 'legislations', this.hydrateReferenceValues(document.legislations, this.legislations))
                })
                this.supplierDocuments.evidenceReports.forEach(document => {
                    this.$set(document, 'legislations', this.hydrateReferenceValues(document.legislations, this.legislations))
                })
            }

            if (this.evidenceReportTypes.length) {
                this.supplierDocuments.evidenceReports.forEach(document => {
                    this.$set(document, 'documentTypes', this.hydrateReferenceValues(document.documentTypes, this.evidenceReportTypes))
                })
            }
        },
        hydrateReferenceValues(values, options) {
            return (values || []).map(value => {
                const id = value && typeof value === 'object' ? value.id : value
                const option = options.find(item => String(item.id) === String(id))

                if (option) {
                    return option
                }

                if (value && typeof value === 'object') {
                    return {
                        ...value,
                        title: value.title || String(id),
                    }
                }

                return {
                    id,
                    title: String(id),
                }
            })
        },
        addDeclarationOfConformity() {
            this.supplierDocuments.declarationsOfConformity.push({
                id: null,
                legislations: [],
                declarationDate: null,
                declarationValidityDate: null,
                document: null,
                file: { name: '' },
            })
            this.markSupplierVerificationDocumentsDirty()
        },
        addCompliantStatement() {
            this.supplierDocuments.compliantStatement.push({
                id: null,
                legislations: [],
                declarationDate: null,
                declarationValidityDate: null,
                document: null,
                file: { name: '' },
            })
            this.markSupplierVerificationDocumentsDirty()
        },
        addEvidenceReport() {
            this.supplierDocuments.evidenceReports.push({
                id: null,
                legislations: [],
                documentTypes: [],
                document: null,
                file: { name: '' },
            })
            this.markSupplierVerificationDocumentsDirty()
        },
        markSupplierVerificationDocumentsDirty() {
            ProductProvider(this.id).markSupplierVerificationDocumentsDirty()
        },
        removeSupplierDocument(collectionName, document) {
            const remove = () => {
                const documents = this.supplierDocuments[collectionName]
                const index = documents.findIndex(item => item === document || (document.id && item.id === document.id))

                if (index >= 0) {
                    documents.splice(index, 1)
                }
            }

            if (!document.id) {
                remove()
                this.markSupplierVerificationDocumentsDirty()
                return
            }

            this.confirmSupplierDocumentDeletion(document, remove)
        },
        removeSupplierInstruction(instructionName) {
            const instruction = this.supplierDocuments[instructionName]
            const clear = () => {
                this.$set(instruction, 'id', null)
                this.$set(instruction, 'description', '')
                this.$set(instruction, 'document', null)
                this.$set(instruction, 'file', null)
            }

            if (!instruction.id) {
                clear()
                this.markSupplierVerificationDocumentsDirty()
                return
            }

            this.confirmSupplierDocumentDeletion(instruction, clear)
        },
        confirmSupplierDocumentDeletion(document, onDeleted) {
            this.$swal({
                title: this.$t('Component(DeleteDocumentPrompt):title'),
                text: this.$t('Component(DeleteDocumentPrompt):text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('Component(DeleteDocumentPrompt):button-delete'),
                cancelButtonText: this.$t('Component(Button):cancel'),
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-outline-secondary ml-1',
                },
                buttonsStyling: false,
            }).then(result => {
                if (!result.isConfirmed) {
                    return
                }

                ProductApi.deleteDocument(this.id, document.id)
                    .then(() => {
                        onDeleted()
                        this.$swal({
                            icon: 'success',
                            title: this.$t('Component(DeleteDocumentAck):title'),
                            text: this.$t('Component(DeleteDocumentAck):text'),
                            customClass: {
                                confirmButton: 'btn btn-primary',
                            },
                        })
                    })
                    .catch(error => {
                        this.$swal({
                            icon: 'error',
                            title: 'Error!',
                            text: this.getErrorMessage(error),
                            customClass: {
                                confirmButton: 'btn btn-primary',
                            },
                            buttonsStyling: false,
                        })
                    })
            })
        },
        isEmpty(type) {
            return this.supplierDocuments[type].length === 0 && this.replyDocuments[type].length === 0
        },
        downloadSupplierDocument(document, done) {
            this.downloadProductDocument(this.id, document)
                .finally(() => {
                    if (done) {
                        done()
                    }
                })
        },
        previewSupplierDocument(document) {
            this.supplierPreviewFile = document
            this.$nextTick(() => {
                this.$refs.supplierPreview.show()
            })
        },
        downloadReplyDocument(document) {
            if (!this.replyDocuments.requestId) {
                return
            }

            this.downloadRepliedDocument(this.replyDocuments.requestId, document)
        },
        previewReplyDocument(document) {
            if (!this.replyDocuments.requestId) {
                return
            }

            this.replyPreviewFile = document
            this.$nextTick(() => {
                this.$refs.replyPreview.show()
            })
        },
        getErrorMessage(error) {
            if (error && error.response && error.response.data) {
                return error.response.data.title || error.response.data.detail || this.$t('Component(Error):default')
            }

            return error && error.message ? error.message : this.$t('Component(Error):default')
        },
    },
}
</script>
